import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkOutlined } from '@mui/icons-material';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import { Button, Tooltip, Typography } from '@mui/material';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import { toMonitoringType } from 'shared/enums/fault-type';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { useOrganisationUnitTreeContext } from 'shared/features/organisation/providers/organisation-unit-tree-provider';
import usePrevious from 'shared/hooks/previous';
import { DeviceType } from 'shared/models/device-type';
import { DeviceDetailsProvider } from 'shared/providers/device-details/providers/device-details-provider';

import DeviceDetailsPopup from 'features/device/device-details/views/device-details-popup.component';
import { useDevices } from 'features/device/devices/hooks/use-devices';
import { useOrganisationUnitsProvider } from 'features/organisation/context/organisation-units-provider';
import OrganisationUnitDevice from 'features/organisation/domain/models/organisation-unit-device';
import styles from './organisation-details-devices-table.component.module.scss';

interface OrganisationUnitDeviceRowItem extends OrganisationUnitDevice {
  isLinked: boolean;
}

type Props = {
  noDevicesText: string;
  linkedDevices: OrganisationUnitDevice[] | undefined;
  unlinkedDevices: OrganisationUnitDevice[] | undefined;
  viewingMode: ViewingMode;
  onUnlinkButtonClicked: (selectedDevice: OrganisationUnitDevice) => void;
  onRelinkButtonClicked: (selectedDevice: OrganisationUnitDevice) => void;
};

const OrganisationDetailsDevicesTable = (props: Readonly<Props>): ReactElement => {
  const { t } = useTranslation('organisation');
  const { t: tDeviceTypes } = useTranslation('deviceTypes');

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();

  const { openDeviceDetailsPopup, isDeviceDetailsPopupOpen } = useDevices();

  const { organisationUnitDetailsHook } = useOrganisationUnitsProvider();

  const { refetch } = useOrganisationUnitTreeContext();

  const previousIsDeviceDetailsPopupOpen = usePrevious(isDeviceDetailsPopupOpen);

  useEffect(() => {
    if (previousIsDeviceDetailsPopupOpen && !isDeviceDetailsPopupOpen) {
      refetch?.();
      organisationUnitDetailsHook.refresh();
      setSelectedDeviceId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeviceDetailsPopupOpen]);

  const renderIcon = (device: OrganisationUnitDevice): ReactElement => {
    const monitoringType: MonitoringType | undefined = toMonitoringType(device.fault);

    return monitoringType ? <MonitoringIcon type={monitoringType} withTooltip visible size={16} /> : <></>;
  };

  const { hasPermission } = usePermissionAuthorization();

  const isDesoDeviceAlreadyLinked = props.linkedDevices?.some((d) => d.type === DeviceType.DesoResidentTransmitter);

  const renderLinkingAction = (device: OrganisationUnitDevice): ReactElement => {
    const isRelinkDisabled = device.type === DeviceType.DesoResidentTransmitter && isDesoDeviceAlreadyLinked;
    const isCurrentDeviceLinked = props.linkedDevices?.some((linkedDevice) => linkedDevice.id === device.id);

    if (hasPermission(Permission.LinkDevice) && isCurrentDeviceLinked) {
      return (
        <>
          {props.viewingMode !== 'viewing' && (
            <Button
              startIcon={<LinkOffOutlinedIcon />}
              onClick={() => props.onUnlinkButtonClicked(device)}
              variant="text"
              data-testid={`unlinkDeviceButtonFor${device.name}`}
            >
              {t('devicesTable.unlinkButtonText')}
            </Button>
          )}
        </>
      );
    }

    return (
      <>
        {props.viewingMode !== 'viewing' && (
          <Tooltip title={isRelinkDisabled ? t('devicesTable.relinkDisabledTooltip') : ''}>
            <span>
              <Button
                startIcon={<LinkOutlined />}
                onClick={() => props.onRelinkButtonClicked(device)}
                disabled={isRelinkDisabled}
                variant="text"
                data-testid={`relinkDeviceButtonFor${device.name}`}
              >
                {t('devicesTable.relinkButtonText')}
              </Button>
            </span>
          </Tooltip>
        )}
      </>
    );
  };

  const columns: ColumnDefinition<OrganisationUnitDeviceRowItem, NestedKeyof<OrganisationUnitDeviceRowItem>>[] = [
    {
      key: 'name',
      label: t('devicesTable.column.name'),
      renderCustomContentProvider: (item) => (
        <div className={styles.organisationDetailsDevicesTableDeviceColumnSpacing}>
          <Typography
            className={[props.viewingMode === 'viewing' ? styles.clickableDevice : '', item.isLinked ? '' : styles.unlinkedDevice]
              .filter(Boolean)
              .join(' ')}
            variant="subtitle1"
            data-testid={`deviceNameTextFor${item.name}`}
          >
            {item.name}
          </Typography>
        </div>
      ),
      disableSort: true,
    },
    {
      key: 'type' as NestedKeyof<OrganisationUnitDeviceRowItem>,
      label: t('devicesTable.column.type'),
      renderCustomContentProvider: (item) => (
        <div className={styles.organisationDetailsDevicesTableTypeColumnSpacing}>
          <Typography
            variant="subtitle1"
            data-testid={`deviceTypeTextFor${item.name}`}
            className={item.isLinked ? '' : styles.unlinkedDevice}
          >
            {item.displayType ?? tDeviceTypes(item.type)}
          </Typography>
        </div>
      ),
      disableSort: true,
    },
    {
      key: 'fault',
      label: '',
      renderCustomContentProvider: (item) => <Typography>{renderIcon(item)}</Typography>,
      disableSort: true,
      tableCellProps: {
        width: '30px',
        className: props.viewingMode === 'viewing' ? 'viewing-mode' : '',
      },
    },
    ...(hasPermission(Permission.LinkDevice)
      ? [
          {
            key: 'id' as NestedKeyof<OrganisationUnitDeviceRowItem>,
            label: props.viewingMode === 'viewing' ? '' : t('devicesTable.column.action'),
            renderCustomContentProvider: (item: OrganisationUnitDeviceRowItem) => (
              <div className={styles.organisationDetailsDevicesTableActionColumnSpacing}>
                {props.viewingMode === 'viewing' ? <></> : <Typography>{renderLinkingAction(item)}</Typography>}
              </div>
            ),
            disableSort: true,
            tableCellProps: {
              className: props.viewingMode === 'viewing' ? 'viewing-mode' : '',
              align: 'right' as const,
              width: '150px',
              style: { padding: 0 },
            },
          },
        ]
      : []),
  ];

  const getTableData = (): RowItem<OrganisationUnitDeviceRowItem>[] => {
    const linkedDevices =
      props.linkedDevices
        ?.map(
          (linkedDevice) =>
            ({
              ...linkedDevice,
              isLinked: true,
            }) as OrganisationUnitDeviceRowItem,
        )
        .map<RowItem<OrganisationUnitDeviceRowItem>>((device) => ({
          data: device,
          isClickable: props.viewingMode === 'viewing',
          showColorOnHover: props.viewingMode === 'viewing',
        })) ?? [];

    const unlinkedDevices =
      props.unlinkedDevices
        ?.map(
          (unlinkedDevice) =>
            ({
              ...unlinkedDevice,
              isLinked: false,
            }) as OrganisationUnitDeviceRowItem,
        )
        .map<RowItem<OrganisationUnitDeviceRowItem>>((device) => ({
          data: device,
          isClickable: props.viewingMode === 'viewing',
          showColorOnHover: props.viewingMode === 'viewing',
        })) ?? [];

    return linkedDevices.concat(unlinkedDevices);
  };

  return (props.linkedDevices && props.linkedDevices.length > 0) || (props.unlinkedDevices && props.unlinkedDevices?.length > 0) ? (
    <>
      <Table
        className={styles.organisationDetailsDevicesTable}
        data={getTableData()}
        columns={columns}
        rowIdentifier={(item) => item.id}
        onItemClick={(item) => {
          setSelectedDeviceId(item.id);
          openDeviceDetailsPopup(true);
        }}
      />
      {selectedDeviceId && (
        <DeviceDetailsProvider deviceId={selectedDeviceId} initialViewingMode={'viewing'}>
          <DeviceDetailsPopup />
        </DeviceDetailsProvider>
      )}
    </>
  ) : (
    <Typography variant="subtitle1">{props.noDevicesText}</Typography>
  );
};

export default OrganisationDetailsDevicesTable;
